import { useState, useMemo, useCallback, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog';
import {
  User,
  Calendar,
  ChevronLeft,
  Check,
  Stethoscope,
  ShieldCheck,
  FileText,
  BookMarked,
  Loader2,
  Search,
  Plus,
} from 'lucide-react';
import { SurgeonTemplate, ScheduledCase, Laterality, SurgeonFacility, LATERALITY_LABELS } from '@/lib/templates/types';
import type { ClinicalSeverityData } from '@/lib/clinicalSeverityTypes';
import type { SpecialtyDomain } from '@/hooks/useActiveSpecialties';
import { format } from 'date-fns';
import { cn } from '@/lib/utils';
import { CptDescriptionSearch } from './CptDescriptionSearch';
import { Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import { getRelevantProcedureTemplates } from '@/lib/surgeon-console/templateRelevance';
import { useCptSearch } from '@/hooks/useCptSearch';
import type { CptSearchEntry } from '@/lib/cptSearch/catalog';
import {
  addMasterTemplateCptCode,
  buildMasterTemplateCptCodeSet,
  buildSmartModifierOptionFromSearchEntry,
  isSmartModifierInMasterTemplate,
  removeMasterTemplateCptCode,
  updateTemplateSmartModifier,
  type SmartModifierOption,
} from '@/lib/surgeon-console/smartModifierTemplate';

// Template requirements preview data
const TEMPLATE_REQUIREMENTS: Record<string, { label: string; bullets: string[] }> = {
  'total knee arthroplasty': {
    label: 'L36007 Compliant',
    bullets: [
      'Auto-includes >3 months failed conservative care',
      'Auto-prompts for KL Grade 3/4',
      'Auto-formats for 2026 CMS/InterQual rules',
    ],
  },
  'total hip arthroplasty': {
    label: 'L36573 Compliant',
    bullets: [
      'Auto-includes Harris Hip Score documentation',
      'Auto-prompts for failed NSAID/PT therapy',
      'Auto-formats for 2026 CMS/InterQual rules',
    ],
  },
  'arthroscopic rotator cuff repair': {
    label: 'Shoulder RCR',
    bullets: [
      'Auto-includes MRI findings & tear size documentation',
      'Auto-prompts for failed conservative therapy (≥6 weeks)',
      'Auto-formats for payer-specific shoulder guidelines',
    ],
  },
  'acl reconstruction': {
    label: 'ACL Recon',
    bullets: [
      'Auto-includes mechanism of injury documentation',
      'Auto-prompts for Lachman/pivot shift findings',
      'Auto-formats graft type and fixation method',
    ],
  },
};

// Unbundled modifier options per procedure family
interface ModifierOption {
  id: string;
  label: string;
  cptCode: string;
  modifier: string;
  description: string;
}

const MODIFIER_OPTIONS: Record<string, ModifierOption[]> = {
  'shoulder': [
    { id: 'debridement', label: 'Extensive Debridement', cptCode: '29823', modifier: '-XS', description: 'Separate structure — separate compartment' },
    { id: 'tenodesis', label: 'Biceps Tenodesis', cptCode: '29828', modifier: '-XS', description: 'Separate structure — biceps tendon' },
    { id: 'subacromial', label: 'Subacromial Decompression', cptCode: '29826', modifier: '-XS', description: 'Separate structure — subacromial space' },
    { id: 'distal_clavicle', label: 'Distal Clavicle Excision', cptCode: '29824', modifier: '-XS', description: 'Separate structure — AC joint' },
    { id: 'limited_debridement', label: 'Limited Debridement', cptCode: '29822', modifier: '-XS', description: 'Separate structure — limited scope' },
  ],
  'knee_arthro': [
    { id: 'chondroplasty', label: 'Chondroplasty (separate compartment)', cptCode: '29877', modifier: '-XS', description: 'Separate structure — separate compartment' },
    { id: 'meniscectomy', label: 'Partial Meniscectomy', cptCode: '29881', modifier: '-XS', description: 'Separate structure — meniscal compartment' },
    { id: 'meniscus_repair', label: 'Meniscus Repair', cptCode: '29882', modifier: '-51', description: 'Multiple procedures — concurrent meniscal repair' },
    { id: 'microfracture', label: 'Microfracture/Abrasion', cptCode: '29879', modifier: '-XS', description: 'Separate structure — chondral surface' },
    { id: 'loose_body', label: 'Loose Body Removal', cptCode: '29874', modifier: '-51', description: 'Multiple procedures — same session' },
  ],
  'knee_total': [
    { id: 'manipulation', label: 'Manipulation Under Anesthesia', cptCode: '27570', modifier: '-XS', description: 'Separate structure — distinct service' },
    { id: 'synovectomy', label: 'Synovectomy', cptCode: '27331', modifier: '-XS', description: 'Separate structure — synovial compartment' },
    { id: 'hardware_removal', label: 'Hardware Removal', cptCode: '20680', modifier: '-51', description: 'Multiple procedures — same session' },
  ],
  'hip_total': [
    { id: 'hardware_removal_hip', label: 'Hardware Removal', cptCode: '20680', modifier: '-51', description: 'Multiple procedures — same session' },
    { id: 'bone_graft', label: 'Bone Grafting', cptCode: '20902', modifier: '-51', description: 'Multiple procedures — structural graft' },
  ],
  'acl': [
    { id: 'meniscus_repair', label: 'Meniscus Repair', cptCode: '29882', modifier: '-51', description: 'Multiple procedures — concurrent meniscal repair' },
    { id: 'chondroplasty_acl', label: 'Chondroplasty', cptCode: '29877', modifier: '-XS', description: 'Separate structure — separate compartment' },
    { id: 'meniscectomy_acl', label: 'Partial Meniscectomy', cptCode: '29881', modifier: '-XS', description: 'Separate structure — meniscal compartment' },
  ],
  'spine': [
    { id: 'instrumentation', label: 'Posterior Instrumentation', cptCode: '22840', modifier: '-51', description: 'Multiple procedures — instrumentation add-on' },
    { id: 'interbody_device', label: 'Interbody Device', cptCode: '22853', modifier: '-51', description: 'Multiple procedures — biomechanical device' },
    { id: 'allograft_morselized', label: 'Allograft (Morselized)', cptCode: '20930', modifier: '-51', description: 'Multiple procedures — bone graft' },
    { id: 'allograft_structural', label: 'Allograft (Structural)', cptCode: '20931', modifier: '-51', description: 'Multiple procedures — structural graft' },
  ],
  // ── Podiatry / Lower Extremity ──────────────────────────────────────────
  'podiatry_foot': [
    { id: 'pod_debridement', label: 'Wound Debridement (Subcutaneous)', cptCode: '11042', modifier: '-XS', description: 'Separate structure — separate wound bed' },
    { id: 'pod_skin_sub', label: 'Skin Substitute Application', cptCode: 'Q4151', modifier: '-RT', description: 'HCPCS skin substitute — units must match wound size' },
    { id: 'pod_hammertoe_addl', label: 'Additional Hammertoe Correction', cptCode: '28285', modifier: '-T2', description: 'Additional digit — distinct procedural service' },
    { id: 'pod_hardware_removal', label: 'Foot Hardware Removal', cptCode: '20680', modifier: '-51', description: 'Multiple procedures — same session' },
  ],
  'podiatry_ankle': [
    { id: 'pod_ank_debridement', label: 'Achilles Tendon Debridement', cptCode: '27654', modifier: '-XS', description: 'Separate structure — tendon sheath' },
    { id: 'pod_ank_synovectomy', label: 'Ankle Synovectomy', cptCode: '27625', modifier: '-XS', description: 'Separate structure — synovial compartment' },
    { id: 'pod_ank_hardware', label: 'Ankle Hardware Removal', cptCode: '20680', modifier: '-51', description: 'Multiple procedures — same session' },
  ],
};

function getModifierFamily(templateName: string, specialty?: SpecialtyDomain): string {
  const lower = templateName.toLowerCase();
  // Podiatry overrides take precedence so ortho keyword bleed-through is blocked.
  if (specialty === 'podiatry') {
    if (lower.includes('ankle') || lower.includes('achilles')) return 'podiatry_ankle';
    return 'podiatry_foot';
  }
  if (lower.includes('rotator cuff') || lower.includes('shoulder') || lower.includes('labr') || lower.includes('bankart')) return 'shoulder';
  if (lower.includes('total knee')) return 'knee_total';
  if (lower.includes('total hip')) return 'hip_total';
  if (lower.includes('acl')) return 'acl';
  if (lower.includes('spine') || lower.includes('fusion') || lower.includes('laminect') || lower.includes('discect') || lower.includes('acdf') || lower.includes('plif')) return 'spine';
  if (lower.includes('knee') || lower.includes('arthroscop') || lower.includes('meniscec')) return 'knee_arthro';
  return 'knee_total';
}

function getRequirementsForTemplate(templateName: string) {
  const lower = templateName.toLowerCase();
  for (const [key, val] of Object.entries(TEMPLATE_REQUIREMENTS)) {
    if (lower.includes(key)) return val;
  }
  return null;
}

// Demo templates for display when DB templates haven't loaded — specialty-aware
const DEMO_TEMPLATES_BY_SPECIALTY: Record<SpecialtyDomain, Array<{ name: string; specialty: string }>> = {
  orthopedics: [
    { name: 'Total Knee Arthroplasty', specialty: 'Orthopedics' },
    { name: 'Total Hip Arthroplasty', specialty: 'Orthopedics' },
    { name: 'Arthroscopic Rotator Cuff Repair', specialty: 'Orthopedics' },
    { name: 'ACL Reconstruction', specialty: 'Orthopedics' },
  ],
  podiatry: [
    { name: 'Skin Substitute Application — Wound Prep (15002)', specialty: 'Podiatry' },
    { name: 'Diabetic Foot Debridement (28005)', specialty: 'Podiatry' },
    { name: 'Achilles Tendon Repair (27658)', specialty: 'Podiatry' },
    { name: 'Ankle Arthroscopy with Debridement (27696)', specialty: 'Podiatry' },
    { name: 'Hammertoe Correction (28270)', specialty: 'Podiatry' },
  ],
};

interface TemplateSelectionStepProps {
  selectedCase: ScheduledCase;
  templates: SurgeonTemplate[];
  selectedTemplate: SurgeonTemplate | null;
  laterality: Laterality | null;
  facilities: SurgeonFacility[];
  selectedFacility: SurgeonFacility | null;
  onSelectTemplate: (t: SurgeonTemplate) => void;
  onSetLaterality: (l: Laterality | null) => void;
  onSetFacility: (f: SurgeonFacility) => void;
  onOpenTemplateWizard: () => void;
  onChangePatient?: () => void;
  /** Active specialty domain — drives template filtering + modifier family selection. */
  specialty?: SpecialtyDomain;
  /** Extracted clinical severity used to surface AI-suggested templates (non-binding). */
  clinicalSeverityData?: ClinicalSeverityData | null;
}

export function TemplateSelectionStep({
  selectedCase,
  templates,
  selectedTemplate,
  laterality,
  facilities,
  selectedFacility,
  onSelectTemplate,
  onSetLaterality,
  onSetFacility,
  onOpenTemplateWizard,
  onChangePatient,
  specialty = 'orthopedics',
  clinicalSeverityData = null,
}: TemplateSelectionStepProps) {
  const [selectedModifiers, setSelectedModifiers] = useState<Set<string>>(new Set());
  const [smartModifierSearch, setSmartModifierSearch] = useState('');
  const [searchedModifierOptions, setSearchedModifierOptions] = useState<SmartModifierOption[]>([]);
  const [masterTemplateCptCodes, setMasterTemplateCptCodes] = useState<Set<string>>(
    () => buildMasterTemplateCptCodeSet(selectedTemplate),
  );
  const [pendingMasterToggle, setPendingMasterToggle] = useState<{ id: string; label: string; adding: boolean; cptCode: string; modifier: string; description: string } | null>(null);
  const [isSavingMaster, setIsSavingMaster] = useState(false);

  const modifierFamily = selectedTemplate ? getModifierFamily(selectedTemplate.template_name, specialty) : (specialty === 'podiatry' ? 'podiatry_foot' : 'knee_total');
  const modifierOptions = MODIFIER_OPTIONS[modifierFamily] || [];
  const allModifierOptions = useMemo(() => {
    const byCode = new Map<string, ModifierOption | SmartModifierOption>();
    [...modifierOptions, ...searchedModifierOptions].forEach(option => {
      if (!byCode.has(option.cptCode)) byCode.set(option.cptCode, option);
    });
    return Array.from(byCode.values());
  }, [modifierOptions, searchedModifierOptions]);
  const requirements = selectedTemplate ? getRequirementsForTemplate(selectedTemplate.template_name) : null;

  // AI-suggested CPT (non-binding hint, surgeon must still click to select).
  // For Podiatry: when extract-clinical-severity reports a wound size, surface
  // CPT 15002 (Wound Prep) as the suggested template — fulfilling the AI-assist
  // requirement without violating Surgeon Sovereignty (no auto-selection).
  const suggestedCpt = useMemo<string | null>(() => {
    if (specialty !== 'podiatry') return null;
    const woundSize = (clinicalSeverityData as any)?.wound_size_sq_cm as number | undefined;
    if (woundSize && woundSize > 0) {
      return '15002';
    }
    return null;
  }, [specialty, clinicalSeverityData]);

  const smartModifierExistingCodes = useMemo(() => {
    const codes = new Set<string>();
    selectedTemplate?.primary_cpts?.forEach(cpt => codes.add(String(cpt.code)));
    selectedTemplate?.secondary_cpts?.forEach(cpt => codes.add(String(cpt.code)));
    selectedTemplate?.common_addons?.forEach(cpt => codes.add(String(cpt.code)));
    allModifierOptions.forEach(option => codes.add(option.cptCode));
    return codes;
  }, [selectedTemplate, allModifierOptions]);

  const { results: smartModifierSearchResults, isSearching: isSearchingSmartModifiers } = useCptSearch(smartModifierSearch, {
    template: selectedTemplate,
    primaryCpt: selectedTemplate?.primary_cpts?.[0]?.code,
    existingCodes: smartModifierExistingCodes,
    limit: 6,
  });

  // Master-template defaults are stable for the selected template ID. Current-case
  // selection can mutate the working template, but it should not rewrite saved status.
  useEffect(() => {
    const nextMasterCodes = buildMasterTemplateCptCodeSet(selectedTemplate);
    setMasterTemplateCptCodes(nextMasterCodes);

    const nextMasterIds = new Set<string>();
    allModifierOptions.forEach(opt => {
      if (isSmartModifierInMasterTemplate(opt, nextMasterCodes)) nextMasterIds.add(opt.id);
    });

    if (nextMasterIds.size > 0) {
      setSelectedModifiers(prev => {
        const next = new Set(prev);
        nextMasterIds.forEach(id => next.add(id));
        return next;
      });
    }
    // Only reset saved master-template membership when a different procedure template is selected.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTemplate?.id]);

  const toggleModifier = (opt: ModifierOption | SmartModifierOption) => {
    const shouldEnable = !selectedModifiers.has(opt.id);
    setSelectedModifiers(prev => {
      const next = new Set(prev);
      if (next.has(opt.id)) next.delete(opt.id);
      else next.add(opt.id);
      return next;
    });

    if (selectedTemplate) {
      onSelectTemplate(updateTemplateSmartModifier(selectedTemplate, opt, shouldEnable));
    }
  };

  const handleAddSmartModifierSearchResult = useCallback((entry: CptSearchEntry) => {
    const option = buildSmartModifierOptionFromSearchEntry(entry);
    setSearchedModifierOptions(prev => (
      prev.some(existing => existing.cptCode === option.cptCode) ? prev : [...prev, option]
    ));
    setSelectedModifiers(prev => {
      const next = new Set(prev);
      next.add(option.id);
      return next;
    });
    if (selectedTemplate) {
      onSelectTemplate(updateTemplateSmartModifier(selectedTemplate, option, true));
    }
    setSmartModifierSearch('');
    toast.success(`Added CPT ${option.cptCode}`, {
      description: 'Added as a Smart Modifier procedure for this case.',
    });
  }, [onSelectTemplate, selectedTemplate]);

  const handleMasterTemplateToggle = useCallback((opt: ModifierOption, currentlyInMaster: boolean) => {
    setPendingMasterToggle({ id: opt.id, label: opt.label, adding: !currentlyInMaster, cptCode: opt.cptCode, modifier: opt.modifier, description: opt.description });
  }, []);

  const confirmMasterToggle = useCallback(async () => {
    if (!pendingMasterToggle || !selectedTemplate || selectedTemplate.id.startsWith('demo-') || selectedTemplate.id.startsWith('search-')) {
      if (pendingMasterToggle && (selectedTemplate?.id.startsWith('demo-') || selectedTemplate?.id.startsWith('search-'))) {
        toast.error('Cannot save to a demo or ad-hoc template. Create a master template first.');
      }
      setPendingMasterToggle(null);
      return;
    }

    setIsSavingMaster(true);
    try {
      // Fetch current template from DB
      const { data: template, error: fetchErr } = await supabase
        .from('surgeon_templates')
        .select('*')
        .eq('id', selectedTemplate.id)
        .single();

      if (fetchErr || !template) throw new Error('Failed to load template');

      const currentVersion = template.version ?? 1;
      const newVersion = currentVersion + 1;
      let newSecondary = Array.isArray(template.secondary_cpts) ? [...template.secondary_cpts] : [];
      let newCommonAddons = Array.isArray(template.common_addons) ? [...template.common_addons] : [];
      let changeDesc = '';

      if (pendingMasterToggle.adding) {
        // Add modifier CPT to secondary_cpts if not present
        if (!newSecondary.some((c: any) => c.code === pendingMasterToggle.cptCode)) {
          newSecondary.push({
            code: pendingMasterToggle.cptCode,
            description: pendingMasterToggle.description,
            default_modifiers: [pendingMasterToggle.modifier.replace('-', '')],
          });
        }
        changeDesc = `Added ${pendingMasterToggle.cptCode} (${pendingMasterToggle.label}) to master template`;
      } else {
        // Remove modifier CPT from saved master-template defaults.
        newSecondary = newSecondary.filter((c: any) => c.code !== pendingMasterToggle.cptCode);
        newCommonAddons = newCommonAddons.filter((c: any) => c.code !== pendingMasterToggle.cptCode);
        changeDesc = `Removed ${pendingMasterToggle.cptCode} (${pendingMasterToggle.label}) from master template`;
      }

      // Save version snapshot
      await supabase.from('surgeon_template_versions').insert({
        template_id: selectedTemplate.id,
        version: newVersion,
        primary_cpts: template.primary_cpts,
        secondary_cpts: newSecondary,
        narrative_template: template.narrative_template,
        common_addons: newCommonAddons,
        change_description: changeDesc,
        changed_by: 'surgeon',
      });

      // Update the master template
      const { error: updateErr } = await supabase
        .from('surgeon_templates')
        .update({
          secondary_cpts: newSecondary,
          common_addons: newCommonAddons,
          version: newVersion,
          updated_at: new Date().toISOString(),
        })
        .eq('id', selectedTemplate.id);

      if (updateErr) throw updateErr;

      // Update local state
      setMasterTemplateCptCodes(prev => pendingMasterToggle.adding
        ? addMasterTemplateCptCode(prev, pendingMasterToggle.cptCode)
        : removeMasterTemplateCptCode(prev, pendingMasterToggle.cptCode));

      toast.success(`Master Template updated to v${newVersion}`);
    } catch (err: any) {
      toast.error(err.message || 'Failed to update master template');
    } finally {
      setIsSavingMaster(false);
      setPendingMasterToggle(null);
    }
  }, [pendingMasterToggle, selectedTemplate]);

  // Specialty-aware demo fallback when no DB templates have loaded yet.
  const allDisplayTemplates = useMemo(() => {
    if (templates.length > 0) return templates;
    const demoSet = DEMO_TEMPLATES_BY_SPECIALTY[specialty] ?? DEMO_TEMPLATES_BY_SPECIALTY.orthopedics;
    return demoSet.map((entry, i) => ({
      id: `demo-${specialty}-${i}`,
      surgeon_id: '',
      template_name: entry.name,
      specialty: entry.specialty,
      body_region: null,
      laterality_required: true,
      primary_cpts: [],
      secondary_cpts: [],
      narrative_template: null,
      common_addons: [],
      diagnosis_codes: [],
      metadata: {},
      version: 1,
      is_active: true,
      is_demo: true,
      created_at: '',
      updated_at: '',
    })) as SurgeonTemplate[];
  }, [templates, specialty]);
  const displayTemplates = useMemo(() => getRelevantProcedureTemplates({
    templates: allDisplayTemplates,
    selectedCase,
    selectedTemplate,
  }), [allDisplayTemplates, selectedCase, selectedTemplate]);

  return (
    <div className="space-y-6">
      {/* ── 0. Patient Context Header ── */}
      <Card className="border-border/60">
        <CardContent className="p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="flex items-center justify-center w-9 h-9 rounded-full bg-primary/10">
                <User className="w-4 h-4 text-primary" />
              </div>
              <div className="flex items-center gap-2 flex-wrap text-sm">
                <span className="font-semibold text-foreground">{selectedCase.patient_name}</span>
                <span className="text-muted-foreground">|</span>
                <span className="text-muted-foreground">DOB: {selectedCase.patient_dob || '—'}</span>
                <span className="text-muted-foreground">|</span>
                <span className="text-muted-foreground">{selectedCase.scheduled_procedure || 'No procedure'}</span>
                <span className="text-muted-foreground">|</span>
                <span className="text-muted-foreground">{selectedCase.payer_name || 'No payer'}</span>
              </div>
            </div>
            {onChangePatient && (
              <Button variant="ghost" size="sm" onClick={onChangePatient} className="text-xs text-muted-foreground hover:text-foreground gap-1">
                <ChevronLeft className="w-3 h-3" />
                Change Patient
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* ── Main Content Grid ── */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* ── 1. Template Selector (Left) ── */}
        <div className="space-y-3">
          <div className="flex items-center gap-2 flex-wrap">
            <FileText className="w-4 h-4 text-primary" />
            <h3 className="text-sm font-semibold text-foreground">1. Select Procedure Template</h3>
            {suggestedCpt && (
              <Badge
                variant="outline"
                className="ml-auto text-[10px] border-primary/40 bg-primary/5 text-primary gap-1"
                title="AI suggestion based on extracted clinical evidence — surgeon must still select."
              >
                <Sparkles className="w-3 h-3" />
                Suggested: CPT {suggestedCpt}
              </Badge>
            )}
          </div>

          {/* CPT Database Search */}
          <CptDescriptionSearch
            onSelect={({ cptCode, description }) => {
              // Create an ad-hoc template from the search result
              const adHocTemplate: SurgeonTemplate = {
                id: `search-${cptCode}`,
                surgeon_id: '',
                template_name: description,
                specialty: 'Orthopedics',
                body_region: null,
                laterality_required: true,
                primary_cpts: [{ code: cptCode, description }],
                secondary_cpts: [],
                narrative_template: null,
                common_addons: [],
                diagnosis_codes: [],
                metadata: { fromSearch: true },
                version: 1,
                is_active: true,
                is_demo: false,
                created_at: '',
                updated_at: '',
              };
              onSelectTemplate(adHocTemplate);
            }}
          />

          {/* Selected Procedure Confirmation */}
          {selectedTemplate && (
            <button
              className={cn(
                'w-full text-left p-3 rounded-lg border transition-all',
                'border-primary bg-primary/5 ring-1 ring-primary/20'
              )}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Stethoscope className="w-3.5 h-3.5 text-primary" />
                  <span className="text-sm font-medium text-foreground">{selectedTemplate.template_name}</span>
                </div>
                <div className="flex items-center justify-center w-5 h-5 rounded-full bg-primary">
                  <Check className="w-3 h-3 text-primary-foreground" />
                </div>
              </div>
              {selectedTemplate.primary_cpts?.[0]?.code && (
                <Badge variant="outline" className="mt-1.5 text-[10px] font-mono text-muted-foreground">
                  CPT {selectedTemplate.primary_cpts[0].code}
                </Badge>
              )}
              {getRequirementsForTemplate(selectedTemplate.template_name) && (
                <Badge variant="outline" className="mt-1.5 ml-1.5 text-[10px] text-muted-foreground">
                  {getRequirementsForTemplate(selectedTemplate.template_name)!.label}
                </Badge>
              )}
              {selectedTemplate.id.startsWith('search-') && (
                <p className="text-[10px] text-muted-foreground mt-1">Ad-hoc procedure via CPT search</p>
              )}
            </button>
          )}

          <div className="flex items-center gap-2 my-1">
            <div className="flex-1 border-t border-border/50" />
            <span className="text-[10px] text-muted-foreground uppercase tracking-widest">relevant template</span>
            <div className="flex-1 border-t border-border/50" />
          </div>

          <div className="space-y-2">
            {displayTemplates.map(t => {
              const isSelected = selectedTemplate?.id === t.id;
              const reqs = getRequirementsForTemplate(t.template_name);
              return (
                <button
                  key={t.id}
                  onClick={() => onSelectTemplate(t)}
                  className={cn(
                    'w-full text-left p-3 rounded-lg border transition-all',
                    isSelected
                      ? 'border-primary bg-primary/5 ring-1 ring-primary/20'
                      : 'border-border hover:border-primary/40 hover:bg-muted/30'
                  )}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Stethoscope className="w-3.5 h-3.5 text-muted-foreground" />
                      <span className="text-sm font-medium">{t.template_name}</span>
                    </div>
                    {isSelected && (
                      <div className="flex items-center justify-center w-5 h-5 rounded-full bg-primary">
                        <Check className="w-3 h-3 text-primary-foreground" />
                      </div>
                    )}
                  </div>
                  {reqs && (
                    <Badge variant="outline" className="mt-1.5 text-[10px] text-muted-foreground">
                      {reqs.label}
                    </Badge>
                  )}
                </button>
              );
            })}
          </div>

          {/* Requirements Preview */}
          {requirements && (
            <div className="rounded-lg bg-muted/40 border border-border/50 p-3 space-y-2">
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Template Requirements Preview</p>
              <ul className="space-y-1.5">
                {requirements.bullets.map((b, i) => (
                  <li key={i} className="flex items-start gap-2 text-xs text-foreground/80">
                    <ShieldCheck className="w-3.5 h-3.5 text-emerald-500 mt-0.5 shrink-0" />
                    <span>{b}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        {/* ── 2. Laterality (Middle) ── */}
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <Calendar className="w-4 h-4 text-primary" />
            <h3 className="text-sm font-semibold text-foreground">2. Specify Laterality</h3>
          </div>

          <ToggleGroup
            type="single"
            value={laterality || ''}
            onValueChange={val => onSetLaterality((val as Laterality) || null)}
            className="w-full"
          >
            {([
              { value: 'left' as Laterality, label: 'Left', modifier: '-LT' },
              { value: 'right' as Laterality, label: 'Right', modifier: '-RT' },
              { value: 'bilateral' as Laterality, label: 'Bilateral', modifier: '-50' },
            ]).map(opt => (
              <ToggleGroupItem
                key={opt.value}
                value={opt.value}
                className={cn(
                  'flex-1 h-12 text-sm font-medium rounded-lg border transition-all',
                  'data-[state=on]:bg-primary data-[state=on]:text-primary-foreground data-[state=on]:border-primary data-[state=on]:shadow-sm',
                  'data-[state=off]:bg-background data-[state=off]:text-muted-foreground data-[state=off]:border-border data-[state=off]:hover:bg-muted/50'
                )}
              >
                {opt.label}
                <Badge
                  variant="outline"
                  className={cn(
                    'ml-2 text-[10px] px-1.5 py-0',
                    laterality === opt.value
                      ? 'border-primary-foreground/30 text-primary-foreground'
                      : 'border-border text-muted-foreground'
                  )}
                >
                  {opt.modifier}
                </Badge>
              </ToggleGroupItem>
            ))}
          </ToggleGroup>

          {laterality && (
            <div className="text-xs text-muted-foreground">
              Modifier <Badge className="bg-primary/10 text-primary border-primary/30 text-[10px] ml-1">{laterality === 'left' ? 'LT' : laterality === 'right' ? 'RT' : '50'}</Badge> will be applied to all primary CPT codes.
            </div>
          )}

          {/* Facility (read-only) */}
          {selectedFacility && (
            <div className="pt-3 border-t border-border/50 space-y-1">
              <p className="text-xs font-medium text-muted-foreground">Facility</p>
              <p className="text-sm font-medium text-foreground">{selectedFacility.facility_name}</p>
            </div>
          )}
        </div>

        {/* ── 3. Smart Modifiers (Right) ── */}
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <ShieldCheck className="w-4 h-4 text-primary" />
            <h3 className="text-sm font-semibold text-foreground">3. Smart Modifiers</h3>
          </div>
          <p className="text-xs text-muted-foreground leading-relaxed">
            Select any additional procedures performed to auto-apply correct NCCI modifiers and protect revenue.
          </p>

          <div className="relative">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={smartModifierSearch}
              onChange={event => setSmartModifierSearch(event.target.value)}
              placeholder="Search CPT code or procedure to add modifier"
              className="h-11 pl-9 pr-9 text-sm"
            />
            {isSearchingSmartModifiers && (
              <Loader2 className="absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 animate-spin text-muted-foreground" />
            )}

            {smartModifierSearch.trim().length >= 2 && smartModifierSearchResults.length > 0 && (
              <div className="absolute z-40 mt-1 max-h-72 w-full overflow-y-auto rounded-lg border border-border bg-popover shadow-lg">
                {smartModifierSearchResults.map((entry, index) => (
                  <button
                    key={`${entry.code}-${entry.source}`}
                    type="button"
                    onClick={() => handleAddSmartModifierSearchResult(entry)}
                    className={cn(
                      'flex w-full items-start gap-2 px-3 py-2.5 text-left transition-colors hover:bg-accent/50',
                      index > 0 && 'border-t border-border/40',
                    )}
                  >
                    <Plus className="mt-0.5 h-3.5 w-3.5 shrink-0 text-primary" />
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <Badge variant="outline" className="font-mono text-[10px] text-primary">
                          {entry.code}
                        </Badge>
                        {entry.plausible && (
                          <Badge className="bg-primary/10 text-primary border-primary/20 text-[10px]">
                            Plausible
                          </Badge>
                        )}
                      </div>
                      <p className="mt-0.5 line-clamp-2 text-sm font-medium text-foreground">
                        {entry.description}
                      </p>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="space-y-2">
            {allModifierOptions.map(opt => {
              const isChecked = selectedModifiers.has(opt.id);
              const isInMaster = isSmartModifierInMasterTemplate(opt, masterTemplateCptCodes);
              return (
                <div key={opt.id} className="space-y-0">
                  <label
                    className={cn(
                      'flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-all',
                      isChecked
                        ? 'border-primary/40 bg-primary/5'
                        : 'border-border hover:border-primary/30 hover:bg-muted/20'
                    )}
                  >
                    <Checkbox
                      checked={isChecked}
                      onCheckedChange={() => toggleModifier(opt)}
                      className="mt-0.5"
                    />
                    <div className="flex-1 space-y-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-sm font-medium text-foreground">{opt.label}</span>
                        <Badge className="bg-sky-100 text-sky-700 border-sky-200 text-[10px] px-1.5 py-0">
                          CPT {opt.cptCode}
                        </Badge>
                        <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200 text-[10px] px-1.5 py-0">
                          Mod {opt.modifier}
                        </Badge>
                      </div>
                      <p className="text-[11px] text-muted-foreground">{opt.description}</p>
                    </div>
                  </label>
                  {/* Master Template toggle row */}
                  <div className="flex items-center gap-2 pl-9 py-1">
                    <Checkbox
                      checked={isInMaster}
                      onCheckedChange={() => handleMasterTemplateToggle(opt, isInMaster)}
                      className="h-3.5 w-3.5"
                    />
                    <button
                      type="button"
                      onClick={() => handleMasterTemplateToggle(opt, isInMaster)}
                      className="flex items-center gap-1 text-[10px] text-muted-foreground hover:text-foreground transition-colors"
                    >
                      <BookMarked className="w-3 h-3" />
                      <span>{isInMaster ? 'In Master Template' : 'Add to Master Template'}</span>
                    </button>
                    {isInMaster && (
                      <Badge variant="outline" className="text-[9px] px-1 py-0 border-emerald-300 text-emerald-600 bg-emerald-50">
                        Bundled
                      </Badge>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {selectedModifiers.size > 0 && (
            <div className="rounded-lg bg-emerald-50 border border-emerald-200 p-3">
              <p className="text-xs font-medium text-emerald-800">
                {selectedModifiers.size} concurrent procedure{selectedModifiers.size > 1 ? 's' : ''} — modifiers auto-applied
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Master Template Confirmation Dialog */}
      <Dialog open={!!pendingMasterToggle} onOpenChange={() => setPendingMasterToggle(null)}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-base">
              <BookMarked className="w-4 h-4 text-primary" />
              {pendingMasterToggle?.adding ? 'Add to Master Template' : 'Remove from Master Template'}
            </DialogTitle>
            <DialogDescription className="text-sm text-muted-foreground pt-2">
              {pendingMasterToggle?.adding
                ? `"${pendingMasterToggle?.label}" will be permanently added to your Master Template. It will be pre-selected for all future cases using this procedure template.`
                : `"${pendingMasterToggle?.label}" will be permanently removed from your Master Template. It will no longer be pre-selected for future cases.`}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" size="sm" onClick={() => setPendingMasterToggle(null)}>
              Cancel
            </Button>
            <Button size="sm" onClick={confirmMasterToggle} disabled={isSavingMaster}>
              {isSavingMaster && <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />}
              {pendingMasterToggle?.adding ? 'Add to Template' : 'Remove from Template'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
